"use client"

import { useCallback, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import partVehicles from '@constants/values/partVehicles'
import PathFinder from '../../../../../next-spl-part-utils/utils/Pathfinder.js'
import { PROPER_NAME_DELIMITER } from '../../../../../next-spl-part-utils/constants/delimiters.js'
import InputGrid from '@components/search/forms/FormWizardContainer'
import Button from '@components/button/Button'
import Icon from '@next-spl-monorepo/shared-components/icon/Icon'
import apiFetcher from '@api/apiFetcher'
import useT from '@hooks/useTranslation'

import inputStyles from '@components/form/new-inputs-for-context-form/Input.module.css'
import styles from '@components/add/forms/newEditForms/partFormPartials/partCategoryPicker.module.css'

export function PartCategoryPicker ({
    allowIntermediate,
    defaultValue,
    depValues,
    onChange,
}) {
    const [path, setPath] = useState(defaultValue ? defaultValue.split(PROPER_NAME_DELIMITER) : [])
    const [vehicle] = depValues

    const hintUrl = useMemo(() => vehicle ? `/api/hints/part_category?pojazd=${vehicle || partVehicles[0]}` : null, [vehicle])
    const { data: categoryTree } = useSWR(hintUrl, apiFetcher)

    const isFinal = path?.exhausted
    const isPickable = Boolean(path.length && (isFinal || allowIntermediate))

    const cancel = () => onChange(null)
    const pickCurrentPath = useCallback(() => {
        if (!isPickable) return
        onChange(pathToString(path))
    }, [isPickable, onChange, path])

    const t = {
        cancel: useT('Anuluj'),
        category: useT('Kategoria części'),
        select: useT('Wybierz'),
    }

    useEffect(() => {
        if (isFinal) {
            pickCurrentPath()
        }
    }, [isFinal, pickCurrentPath])

    return (
        <>
            { path.length > 0 && <div className={styles.picked}>{path.join(PROPER_NAME_DELIMITER)} {isFinal && <Icon>done</Icon>}</div> }

            <h3>{t.category}</h3>
            <Lookup categoryTree={categoryTree} setPath={setPath}>
                <Navigate categoryTree={categoryTree} path={path} setPath={setPath} />
            </Lookup>

            <br/>
            <InputGrid.FlexDistributed style={{background:'#fff',bottom:-16,paddingBlock:16,position:'sticky'}}>
                { <Button onClick={cancel} blue>{t.cancel}</Button>}
                { isPickable && <Button icon="done" onClick={pickCurrentPath} teal>{t.select}</Button>}
            </InputGrid.FlexDistributed>
        </>
    )
}

function Navigate ({ categoryTree, path, setPath }) {
    const t = {
        back: useT('Powrót')
    }

    if (!categoryTree) return null

    const currentTier = path.reduce((acc, key) => {
        if (acc && acc[key]) return acc[key]
    }, categoryTree || {})

    const isFirst = currentTier === categoryTree
    const isFinal = currentTier == null

    const back = () => setPath((path) => path.slice(0, path.length - 1))
    const ventureInto = (nextPath) => () => setPath((path) => {
        const newPath = path.concat(nextPath)
        if (currentTier[nextPath] == null) newPath.exhausted = true
        return newPath
    })

    return <div>
        {
            !isFirst && <div
                className={styles.back}
                onClick={back}
                role="button"
            >
                <Icon>chevron_left</Icon> {t.back}
            </div>
        }
        {
            !isFinal && Object.keys(currentTier).map((path, idx) => (
                <div
                    className={styles.pick}
                    key={`${path}_${idx}`}
                    onClick={ventureInto(path)}
                    role="button"
                >
                        {path} <Icon>chevron_right</Icon>
                </div>
            ))
        }
    </div>
}

function Lookup ({ categoryTree, children, setPath }) {
    const t = {
        notFound: useT('Brak wyników'),
        pick: useT('Spróbuj wybrać z listy'),
    }

    const [lookupText, setLookupText] = useState('')
    const [lookupPaths, setLookupPaths] = useState([])
    const pathFinder = useMemo(() => new PathFinder(categoryTree, 6), [categoryTree])

    const _setPath = useCallback((path) => {
        setPath(path)
        setLookupText('')
        setLookupPaths([])
    }, [setPath, setLookupPaths, setLookupText])

    const lookup = ({ target: { value }}) => {
        if (!value) return setLookupPaths([])
        const { paths } = pathFinder.findPathsTo(value)
        setLookupText(value)
        setLookupPaths(paths)
    }

    const hasSearchPhrase = lookupText.length > 0
    const hasSearchResults = lookupPaths.length > 0
    const nothingFound = hasSearchPhrase && !hasSearchResults

    return <>
        <div className={`${inputStyles['input-outer']} ${inputStyles.solid}`}>
            <input className={inputStyles.input} onChange={lookup} type="text" />
            <span className={inputStyles.icon}><Icon>search</Icon></span>
        </div>

        <br/>
        { nothingFound && <p style={{textAlign:'center'}}>{t.notFound}. {t.pick}:</p> }
        { lookupPaths.map((path, idx) => <LookupSelectPath key={idx} path={path} setPath={_setPath} text={lookupText} />) }

        <br/>
        { !hasSearchResults && children }
    </>
}

function LookupSelectPath ({ path, setPath, text }) {
    return <div className={styles['lookup-path']} onClick={()=>setPath(path)} role="button">
        {path.map((name, idx) => lookupNameToJSX(name, text, idx))}
    </div>
}

function lookupNameToJSX (name, text, key) {
    if (typeof name !== 'string') return null
    const _name = (name || '').toLowerCase()
    const _text = (text || '').toLowerCase()
    if (_text && _name.includes(_text)) {
        const textStartIdx = _name.indexOf(_text)
        const textEndIdx = textStartIdx + _text.length
        const part1 = name.slice(0, textStartIdx)
        const text1 = name.slice(textStartIdx, textEndIdx)
        const part2 = name.slice(textEndIdx)

        return <span key={key}>{part1}<b>{text1}</b>{part2}</span>

    } else {
        return <span key={key}>{name}</span>
    }
}

function pathToString (path) {
    return path.join(PROPER_NAME_DELIMITER)
}